import React, { useState, useEffect, useMemo } from 'react';
import { useRegion } from '../context/RegionContext';
import { useLanguage } from '../context/LanguageContext';

interface FooterLocationModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const getCountryFlag = (countryCode?: string): string => {
    if (!countryCode || countryCode.length !== 2) return '🌐';
    try {
        const code = countryCode.toUpperCase();
        return String.fromCodePoint(
            127397 + code.charCodeAt(0),
            127397 + code.charCodeAt(1)
        );
    } catch {
        return '🌐';
    }
};

const DEFAULT_REGIONS = [
    { code: 'IN', name: 'India', currency: 'INR', currencySymbol: '₹' },
    { code: 'US', name: 'United States', currency: 'USD', currencySymbol: '$' },
    { code: 'DE', name: 'Germany', currency: 'EUR', currencySymbol: '€' },
    { code: 'FR', name: 'France', currency: 'EUR', currencySymbol: '€' },
    { code: 'CH', name: 'Switzerland', currency: 'EUR', currencySymbol: '€' },
    { code: 'IT', name: 'Italy', currency: 'EUR', currencySymbol: '€' },
    { code: 'DK', name: 'Denmark', currency: 'EUR', currencySymbol: '€' },
    { code: 'SE', name: 'Sweden', currency: 'EUR', currencySymbol: '€' },
    { code: 'NL', name: 'Netherlands', currency: 'EUR', currencySymbol: '€' },
    { code: 'BE', name: 'Belgium', currency: 'EUR', currencySymbol: '€' },
    { code: 'LU', name: 'Luxembourg', currency: 'EUR', currencySymbol: '€' },
];

export default function FooterLocationModal({ isOpen, onClose }: FooterLocationModalProps) {
    const { selectedCountry, regions, confirmRegion } = useRegion();
    const { t } = useLanguage();
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    const displayRegions = useMemo(() => {
        const base = (regions && regions.length > 0) ? regions : DEFAULT_REGIONS;
        const normalized = base.map((r: any) => ({
            code: r.code || r.countryCode || r.country || 'IN',
            name: r.name || r.country || 'Unknown',
            currency: r.currency || 'USD',
            currencySymbol: r.currencySymbol || '$',
        }));

        if (!searchQuery.trim()) return normalized;
        const q = searchQuery.toLowerCase().trim();
        return normalized.filter((r: any) => 
            r.name.toLowerCase().includes(q) || 
            r.code.toLowerCase().includes(q) ||
            r.currency.toLowerCase().includes(q)
        );
    }, [regions, searchQuery]);

    if (!isOpen) return null;

    const handleSelect = (code: string) => {
        confirmRegion(code);
        onClose();
    };

    return (
        <div 
            className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            onClick={onClose}
        >
            <div 
                className="relative w-full max-w-lg rounded-2xl border border-slider bg-bg p-6 shadow-2xl transition-all duration-300 max-h-[85vh] flex flex-col overflow-hidden text-text"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between pb-4 border-b border-slider">
                    <div>
                        <h3 className="text-xl font-bold text-text m-0 flex items-center gap-2">
                            <span>📍</span>
                            <span>{t('footer.selectLocationTitle', 'Select Your Region')}</span>
                        </h3>
                        <p className="text-xs text-text opacity-70 mt-1 mb-0 leading-relaxed">
                            {t('footer.locationDescription', 'Choose your location to update store currency, pricing, and availability.')}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-full w-8 h-8 flex items-center justify-center text-text opacity-60 hover:opacity-100 hover:bg-hover transition-all border-none bg-transparent cursor-pointer"
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                {/* Search */}
                <div className="py-3">
                    <div className="relative flex items-center">
                        <span className="absolute left-3 text-sm opacity-50">🔍</span>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t('footer.searchRegionPlaceholder', 'Search country or currency...')}
                            className="w-full pl-9 pr-4 py-2 text-sm rounded-xl border border-slider bg-hover text-text outline-none focus:border-primary-cyan transition-colors"
                            autoFocus
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 text-xs opacity-50 hover:opacity-100 border-none bg-transparent text-text cursor-pointer"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                {/* Country List */}
                <div className="overflow-y-auto flex-1 pr-1 space-y-1.5 custom-scrollbar max-h-[380px]">
                    {displayRegions.length === 0 ? (
                        <div className="py-8 text-center text-sm opacity-60">
                            No matching regions found.
                        </div>
                    ) : (
                        displayRegions.map((region: any) => {
                            const isSelected = selectedCountry === region.code;
                            const flag = getCountryFlag(region.code);
                            return (
                                <button
                                    key={region.code}
                                    onClick={() => handleSelect(region.code)}
                                    className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left cursor-pointer ${
                                        isSelected
                                            ? 'border-primary-cyan bg-primary-cyan/10 font-medium'
                                            : 'border-transparent bg-hover hover:border-slider hover:bg-hover/80'
                                    }`}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="text-2xl leading-none">{flag}</span>
                                        <div className="truncate">
                                            <p className="m-0 text-sm font-semibold text-text truncate">
                                                {region.name}
                                            </p>
                                            <p className="m-0 text-xs text-text opacity-60">
                                                {region.code} · {region.currency} ({region.currencySymbol})
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className="text-xs px-2 py-0.5 rounded-md bg-slider text-text font-mono font-medium opacity-80">
                                            {region.currencySymbol} {region.currency}
                                        </span>
                                        {isSelected && (
                                            <span className="w-5 h-5 rounded-full bg-primary-cyan text-white text-xs flex items-center justify-center font-bold">
                                                ✓
                                            </span>
                                        )}
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>

                {/* Footer notes */}
                <div className="pt-3 mt-2 border-t border-slider flex items-center justify-between text-xs opacity-60">
                    <span>Active: <strong>{selectedCountry || 'IN'}</strong></span>
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 rounded-lg border border-slider bg-hover text-text hover:border-primary-cyan cursor-pointer transition-colors"
                    >
                        {t('common.close', 'Close')}
                    </button>
                </div>
            </div>
        </div>
    );
}
